<script lang="ts">
  import { onMount } from 'svelte';
  import type { DeviceSkuSummary } from '@ucc/shared';
  import { ApiError, fetchDeviceSkus, registerDevice } from '$lib/api';
  import { deviceHref } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let uid = $state('');
  let sku = $state('');
  let skuOptions = $state<DeviceSkuSummary[]>([]);
  let error = $state<string | null>(null);
  let submitting = $state(false);
  let issuedPuk = $state<string | null>(null);
  let registeredId = $state<string | null>(null);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  onMount(() => {
    void loadSkus();
  });

  async function loadSkus() {
    try {
      const res = await fetchDeviceSkus();
      skuOptions = (res.skus ?? []).filter((s) => s.active !== false);
      if (sku && !skuOptions.some((s) => s.code === sku)) {
        sku = '';
      }
    } catch {
      skuOptions = [];
    }
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    error = null;
    issuedPuk = null;
    registeredId = null;
    if (!isStaff) {
      error = 'Only staff can register devices.';
      return;
    }
    const uidNorm = uid.trim().toUpperCase();
    const skuNorm = sku.trim();
    if (!/^[0-9A-F]{8}$/.test(uidNorm)) {
      error = 'UID must be 8 hexadecimal characters (e.g. 08AA0001).';
      return;
    }
    if (!skuNorm) {
      error = 'Select a SKU from the list. Create new SKUs under Settings → SKU Manager.';
      return;
    }
    if (!skuOptions.some((s) => s.code === skuNorm)) {
      error = 'Unknown or inactive SKU. Open SKU Manager to add it first.';
      return;
    }

    submitting = true;
    try {
      const res = await registerDevice({
        uid: uidNorm,
        sku: skuNorm,
      });
      registeredId = res.device.id;
      issuedPuk = res.device.puk ?? null;
      if (!issuedPuk) {
        window.location.hash = deviceHref(res.device.id);
      }
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Register failed. Try again.';
    } finally {
      submitting = false;
    }
  }

  async function copyPuk() {
    if (!issuedPuk) return;
    try {
      await navigator.clipboard.writeText(issuedPuk);
    } catch {
      /* ignore */
    }
  }
</script>

<div class="page">
  <header class="head">
    <div>
      <a class="back" href="#/devices">← Devices</a>
      <h2>Register device</h2>
      <p class="sub">
        Add a factory unit with hardware UID and a SKU from the catalog. The system generates a
        one-time PUK for the client to claim later.
      </p>
    </div>
  </header>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can register hardware into inventory.</p>
      <a href="#/devices">Back to devices</a>
    </Card>
  {:else if issuedPuk && registeredId}
    <Card title="Device registered" subtitle="Copy the PUK now — clients need it to claim">
      <dl class="facts">
        <div>
          <dt>UID</dt>
          <dd class="mono">{uid.trim().toUpperCase()}</dd>
        </div>
        <div>
          <dt>SKU</dt>
          <dd class="mono">{sku.trim()}</dd>
        </div>
        <div>
          <dt>PUK (one-time)</dt>
          <dd class="mono puk">{issuedPuk}</dd>
        </div>
      </dl>
      <div class="actions">
        <Button type="button" onclick={() => void copyPuk()}>Copy PUK</Button>
        <a class="link-btn" href={deviceHref(registeredId)}>Open device</a>
        <a class="cancel" href="#/devices">Back to list</a>
      </div>
      <p class="hint">
        Share the PUK with the client. After they claim, the PUK is consumed and no longer shown.
      </p>
    </Card>
  {:else}
    <Card title="Factory registration" subtitle="UID is 8 hex · SKU from catalog only">
      <form class="form" autocomplete="off" onsubmit={(e) => void onSubmit(e)}>
        <label class="field">
          <span class="label">Device UID</span>
          <input
            class="input mono"
            id="reg-device-uid"
            name="ucc_reg_uid"
            type="text"
            inputmode="text"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            maxlength={8}
            placeholder="08AA0001"
            bind:value={uid}
            disabled={submitting}
            required
          />
        </label>

        <label class="field">
          <span class="label">SKU ID</span>
          <select
            class="input mono"
            id="reg-device-sku"
            name="ucc_reg_sku"
            bind:value={sku}
            disabled={submitting || skuOptions.length === 0}
            required
          >
            <option value="" disabled={sku !== ''}>
              {skuOptions.length === 0 ? 'No active SKUs — add in SKU Manager' : 'Select a SKU…'}
            </option>
            {#each skuOptions as opt (opt.id)}
              <option value={opt.code}>
                {opt.code}{opt.label ? ` — ${opt.label}` : ''}
              </option>
            {/each}
          </select>
          <span class="help">
            Pick an active SKU only.
            <a href="#/settings/skus">Manage SKUs</a>
          </span>
        </label>

        {#if error}
          <p class="err" role="alert">{error}</p>
        {/if}

        <div class="actions">
          <Button type="submit" disabled={submitting || skuOptions.length === 0}>
            {submitting ? 'Registering…' : 'Register device'}
          </Button>
          <a class="cancel" href="#/devices">Cancel</a>
        </div>
      </form>
    </Card>
  {/if}
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5, 1.25rem);
    max-width: 32rem;
  }
  .head {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: 0.9375rem;
    line-height: 1.45;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--text-muted);
  }
  .help {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .help a {
    color: var(--accent, #2563eb);
  }
  .input {
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.55rem 0.75rem;
    font: inherit;
    background: var(--surface);
    color: var(--text);
  }
  .input.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0.04em;
  }
  select.input {
    cursor: pointer;
  }
  .input:focus {
    outline: 2px solid color-mix(in srgb, var(--accent, #2563eb) 55%, transparent);
    outline-offset: 1px;
  }
  .input:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .err {
    margin: 0;
    color: var(--danger, #ef4444);
    font-size: 0.875rem;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.25rem;
    flex-wrap: wrap;
  }
  .cancel {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
  .muted {
    color: var(--text-muted);
  }
  .facts {
    display: grid;
    gap: 0.75rem;
    margin: 0 0 1rem;
  }
  .facts dt {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    margin: 0 0 0.15rem;
  }
  .facts dd {
    margin: 0;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0.03em;
  }
  .puk {
    user-select: all;
    font-size: 1.05rem;
    font-weight: 600;
  }
  .hint {
    margin: 0.75rem 0 0;
    font-size: 0.8125rem;
    color: var(--text-muted);
    line-height: 1.4;
  }
  .link-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 0.9rem;
    border-radius: 8px;
    background: var(--accent, #2563eb);
    color: #fff;
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 600;
  }
</style>
